import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, ClipboardList, Wallet, Star, Check, X, Ban, Sparkles, CalendarDays, FileText, ChevronDown, Undo2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveFile = (u) => (!u ? u : (u.startsWith("http") ? u : `${BACKEND}${u}`));

export default function AdminDashboard() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");

  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  const trainers = useQuery({ queryKey: ["admin-trainers"], queryFn: async () => (await api.get("/admin/trainers")).data });
  const users = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const bookings = useQuery({ queryKey: ["admin-bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
    qc.invalidateQueries({ queryKey: ["admin-trainers"] });
  };

  const approve = useMutation({ mutationFn: (id) => api.post(`/admin/trainers/${id}/approve`), onSuccess: () => { toast.success("Approved"); invalidateAll(); } });
  const reject  = useMutation({ mutationFn: (id) => api.post(`/admin/trainers/${id}/reject`), onSuccess: () => { toast.success("Rejected"); invalidateAll(); } });
  const feature = useMutation({ mutationFn: ({id, on}) => api.post(`/admin/trainers/${id}/feature?featured=${on}`), onSuccess: () => { toast.success("Updated"); invalidateAll(); } });
  const suspend = useMutation({ mutationFn: ({id, on}) => api.post(`/admin/users/${id}/suspend?suspended=${on}`), onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["admin-users"] }); } });
  const refund = useMutation({
    mutationFn: (id) => api.post(`/admin/bookings/${id}/refund`),
    onSuccess: () => { toast.success("Refund issued via Stripe"); qc.invalidateQueries({ queryKey: ["admin-bookings"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Refund failed"),
  });

  const [editTrainer, setEditTrainer] = useState(null);
  const saveTrainer = useMutation({
    mutationFn: ({ id, body }) => api.put(`/admin/trainers/${id}`, body),
    onSuccess: () => { toast.success("Trainer updated"); setEditTrainer(null); invalidateAll(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const pending = (trainers.data || []).filter((t) => t.status === "pending");
  const approved = (trainers.data || []).filter((t) => t.status === "approved");

  return (
    <div className="container-fl py-10">
      <div className="mb-8">
        <div className="overline mb-2">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Platform control room</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Stat label="Users" value={stats.data?.users || 0} icon={<Users size={14}/>}/>
        <Stat label="Trainers" value={stats.data?.trainers || 0} icon={<ClipboardList size={14}/>}/>
        <Stat label="Pending" value={stats.data?.pending_trainers || 0} icon={<Sparkles size={14}/>}/>
        <Stat label="Bookings" value={stats.data?.bookings || 0} icon={<CalendarDays size={14}/>}/>
        <Stat label="Reviews" value={stats.data?.reviews || 0} icon={<Star size={14}/>}/>
        <Stat label="Gross paid" value={`€${(stats.data?.gross_paid || 0).toFixed(2)}`} icon={<Wallet size={14}/>}/>
      </div>

      <div className="card-fl p-5 mb-8 !bg-neutral-950 !border-neutral-950">
        <div className="grid sm:grid-cols-3 gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-1">Platform earnings ({Math.round((stats.data?.platform_fee_pct || 0) * 100)}%)</div>
            <div className="text-3xl font-black text-emerald-400" data-testid="admin-platform-fees">€{(stats.data?.platform_fees || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-1">Trainer payouts owed</div>
            <div className="text-3xl font-black text-neutral-100">€{(stats.data?.trainer_net || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-1">Gross processed</div>
            <div className="text-3xl font-black text-white">€{(stats.data?.gross_paid || 0).toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { k: "overview", label: `Approvals (${pending.length})` },
          { k: "trainers", label: "Trainers" },
          { k: "users", label: "Users" },
          { k: "bookings", label: "Bookings" },
        ].map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className={`pill ${tab === t.k ? "pill-active" : ""}`}>{t.label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div data-testid="pending-list" className="space-y-3">
          {pending.length === 0 ? (
            <div className="border border-dashed border-neutral-200 rounded-xl p-10 text-center text-neutral-500">No pending trainer approvals.</div>
          ) : pending.map((t) => (
            <PendingRow key={t.id} t={t} onApprove={() => approve.mutate(t.id)} onReject={() => reject.mutate(t.id)} />
          ))}
        </div>
      )}

      {tab === "trainers" && (
        <div className="space-y-3">
          {approved.map((t) => (
            <div key={t.id} className="card-fl p-5 flex items-center justify-between gap-4">
              <div>
                <div className="font-bold">{t.name} {t.featured && <span className="text-xs text-emerald-700 ml-2">FEATURED</span>} {t.away && <span className="text-xs text-amber-700 ml-2">AWAY</span>}</div>
                <div className="text-sm text-neutral-500">{t.email} · {t.city || "—"}, {t.county || "—"} · €{t.hourly_rate}</div>
              </div>
              <div className="flex gap-2">
                <a href={`/trainer/${t.slug || t.id}`} target="_blank" rel="noreferrer"
                   data-testid={`view-${t.id}`} className="btn-secondary !py-1.5 !px-3 !text-xs">
                  View
                </a>
                <a href={`mailto:${t.email}?subject=${encodeURIComponent(`FitLink Ireland — ${t.name}`)}&body=${encodeURIComponent(`Hi ${t.name},\n\n`)}`}
                   data-testid={`email-${t.id}`} className="btn-secondary !py-1.5 !px-3 !text-xs">
                  Email
                </a>
                <button data-testid={`edit-${t.id}`} onClick={() => setEditTrainer(t)}
                        className="btn-secondary !py-1.5 !px-3 !text-xs">
                  Edit
                </button>
                <button data-testid={`feature-${t.id}`} onClick={() => feature.mutate({ id: t.id, on: !t.featured })} className="btn-secondary !py-1.5 !px-3 !text-xs">
                  <Sparkles size={12}/> {t.featured ? "Unfeature" : "Feature"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-2">
          {(users.data || []).map((u) => (
            <div key={u.id} className="card-fl p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{u.name} <span className="text-xs text-neutral-500 ml-2">{u.role}</span></div>
                <div className="text-xs text-neutral-500">{u.email}</div>
              </div>
              <button data-testid={`suspend-${u.id}`} onClick={() => suspend.mutate({ id: u.id, on: !u.suspended })} className="btn-secondary !py-1.5 !px-3 !text-xs">
                <Ban size={12}/> {u.suspended ? "Unsuspend" : "Suspend"}
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-2">
          {(bookings.data || []).map((b) => (
            <div key={b.id} className="card-fl p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{b.service_title} — €{b.price}</div>
                <div className="text-xs text-neutral-500">{b.customer_name} → {b.trainer_name} · {b.date} {b.slot}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-widest font-bold rounded-full px-2.5 py-1 bg-neutral-100">{b.status.replace("_"," ")}</span>
                {b.paid && b.status !== "refunded" && b.payment_status !== "free" && Number(b.price) > 0 && (
                  <button data-testid={`refund-${b.id}`} onClick={() => { if (window.confirm(`Refund €${b.price} to ${b.customer_name}?`)) refund.mutate(b.id); }}
                          className="btn-secondary !py-1.5 !px-3 !text-xs">
                    <Undo2 size={12}/> Refund €{b.price}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editTrainer && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="admin-edit-modal"
             onClick={() => setEditTrainer(null)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-lg mb-4">Edit {editTrainer.name}</div>
            <div className="space-y-3">
              <AdminField label="Display name" value={editTrainer.name}
                          onChange={(v) => setEditTrainer({ ...editTrainer, name: v })}/>
              <AdminField label="City" value={editTrainer.city}
                          onChange={(v) => setEditTrainer({ ...editTrainer, city: v })}/>
              <AdminField label="County" value={editTrainer.county}
                          onChange={(v) => setEditTrainer({ ...editTrainer, county: v })}/>
              <AdminField label="Hourly rate (€)" type="number" value={editTrainer.hourly_rate}
                          onChange={(v) => setEditTrainer({ ...editTrainer, hourly_rate: Number(v) })}/>
              <AdminField label="Phone" value={editTrainer.phone}
                          onChange={(v) => setEditTrainer({ ...editTrainer, phone: v })}/>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Bio</label>
                <textarea rows={4} value={editTrainer.bio || ""}
                          onChange={(e) => setEditTrainer({ ...editTrainer, bio: e.target.value })}
                          className="mt-1 w-full rounded-md border border-neutral-200 p-3 text-sm outline-none focus:border-neutral-900"/>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditTrainer(null)} className="btn-secondary flex-1">Cancel</button>
              <button data-testid="admin-save-trainer"
                      onClick={() => saveTrainer.mutate({
                        id: editTrainer.id,
                        body: {
                          bio: editTrainer.bio || "",
                          city: editTrainer.city || "",
                          county: editTrainer.county || "",
                          hourly_rate: Number(editTrainer.hourly_rate) || 0,
                          phone: editTrainer.phone || "",
                        },
                      })}
                      className="btn-primary flex-1" disabled={saveTrainer.isPending}>
                {saveTrainer.isPending ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminField({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="mt-1 h-11 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
    </div>
  );
}

const Stat = ({ label, value, icon }) => (
  <div className="card-fl p-4">
    <div className="text-neutral-500 text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5">{icon} {label}</div>
    <div className="text-xl font-black mt-1">{value}</div>
  </div>
);

function PendingRow({ t, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const quals = t.qualifications || [];
  return (
    <div className="card-fl p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <div className="font-bold">{t.name}</div>
          <div className="text-sm text-neutral-500">{t.email} · {t.city || "—"}, {t.county || "—"}</div>
          <div className="text-xs text-neutral-500 mt-1">Rate: €{t.hourly_rate} · {(t.specialities || []).join(", ") || "no specialities"}</div>
          {t.bio && <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{t.bio}</div>}
        </div>
        <div className="flex gap-2 items-start">
          <button onClick={() => setOpen((v) => !v)} className="btn-secondary !py-1.5 !px-3 !text-xs">
            <FileText size={12}/> Docs ({quals.length}) <ChevronDown size={12} className={open ? "rotate-180 transition" : "transition"}/>
          </button>
          <button data-testid={`approve-${t.id}`} onClick={onApprove} className="btn-accent !py-1.5 !px-3 !text-xs"><Check size={12}/> Approve</button>
          <button data-testid={`reject-${t.id}`} onClick={onReject} className="btn-secondary !py-1.5 !px-3 !text-xs"><X size={12}/> Reject</button>
        </div>
      </div>
      {open && (
        <div className="mt-4 border-t border-neutral-200 pt-4">
          <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Uploaded qualifications</div>
          {quals.length === 0 ? (
            <div className="text-sm text-neutral-500">No qualifications uploaded yet.</div>
          ) : (
            <ul className="space-y-2">
              {quals.map((q, i) => (
                <li key={q.id || i} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-semibold">{q.title}</span>
                    <span className="text-neutral-500"> — {[q.issuer, q.year].filter(Boolean).join(" · ") || "no metadata"}</span>
                  </div>
                  {q.file_url && (
                    <a href={resolveFile(q.file_url)} target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold underline">View file</a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
